/**
 * EventRow — collapsible row for a TriggerEvent with grouped rule outcomes.
 */
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faCheck,
  faMinus,
  faXmark,
  faTriangleExclamation,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { BulkApprovalControls } from "./BulkApprovalControls";
import { Badge } from "@/components/ui/badge/Badge";
import type {
  TriggerEventRow as TriggerEventRowType,
  TriggerEventRuleOutcome,
  TriggerRuleOutcome,
} from "@/services/api/agentic-v2/types";
import { ApprovalControls } from "./ApprovalControls";
import { ClientContextLine } from "./ClientContextLine";
import {
  activePendingCount,
  isStillPending,
  pendingIdFromOutcome,
  pendingIdsForEvent,
  resolvedRanCount,
  wasApprovedSinceLogged,
} from "./reviewQueue";

interface EventRowProps {
  event: TriggerEventRowType;
  onDecided?: () => void;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  showApprovals?: boolean;
  defaultOpen?: boolean;
  /** Live pending queue — reconciles stale "pending" in event snapshots. */
  activePendingIds: ReadonlySet<string>;
}

type BadgeColor = "green" | "slate" | "amber" | "red";

const OUTCOME_META: Record<
  TriggerRuleOutcome,
  { label: string; color: BadgeColor; icon: typeof faCheck }
> = {
  matched: { label: "Ran", color: "green", icon: faCheck },
  pending_approval: { label: "Awaiting approval", color: "amber", icon: faMinus },
  skipped_dry_run: { label: "Dry run", color: "slate", icon: faMinus },
  skipped_condition: { label: "Condition false", color: "slate", icon: faMinus },
  skipped_no_fa_client: { label: "No fa_client", color: "amber", icon: faXmark },
  skipped_no_mission: { label: "No mission", color: "amber", icon: faXmark },
  error_condition: { label: "Condition error", color: "red", icon: faTriangleExclamation },
  error_action: { label: "Action error", color: "red", icon: faTriangleExclamation },
  error_unknown_action: { label: "Unknown action", color: "red", icon: faTriangleExclamation },
};

const APPROVED_META = {
  label: "Approved",
  color: "green" as const,
  icon: faCheck,
};

function ruleMeta(
  outcome: TriggerEventRuleOutcome,
  pendingIds: ReadonlySet<string>
) {
  if (wasApprovedSinceLogged(outcome, pendingIds)) return APPROVED_META;
  return OUTCOME_META[outcome.outcome];
}

export function EventRow({
  event,
  onDecided,
  onApprove,
  onReject,
  showApprovals = true,
  defaultOpen = false,
  activePendingIds,
}: EventRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  const time = new Date(event.timestamp);
  const pendingCount = activePendingCount(event, activePendingIds);
  const ranCount = resolvedRanCount(event, activePendingIds);
  const rulesTotal = event.rules_total;
  const rulesToShow =
    showApprovals && pendingCount > 0
      ? event.rules.filter((r) => isStillPending(r, activePendingIds))
      : event.rules;

  return (
    <div
      className="bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/60 rounded-lg"
      data-testid="event-row"
      data-focus-ref={`trigger_event:${event._id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/30 rounded-lg transition-colors"
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`text-slate-400 text-xs transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span className="text-xs text-slate-500 dark:text-slate-400 w-16 flex-shrink-0">
          {formatTime(time)}
        </span>
        <div className="flex-1 min-w-0">
          <ClientContextLine
            displayName={event.client_display_name}
            accountUuid={event.plannr_account_uuid}
            className="mb-0.5"
          />
          <code className="text-xs font-mono text-slate-700 dark:text-slate-200 truncate block">
            {event.event_type}
          </code>
          <PayloadSummary payload={event.payload} />
        </div>
        {rulesTotal > 0 ? (
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge color={ranCount > 0 ? "green" : "slate"} size="sm">
              {ranCount}/{rulesTotal} ran
            </Badge>
            {pendingCount > 0 && (
              <Badge color="amber" size="sm">
                {pendingCount} pending
              </Badge>
            )}
          </div>
        ) : (
          <Badge color="zinc" size="sm">
            No rules
          </Badge>
        )}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-100 dark:border-slate-700/60">
          {showApprovals && pendingCount > 1 && (
            <BulkApprovalControls
              pendingIds={pendingIdsForEvent(event, activePendingIds)}
              onDecided={onDecided ?? (() => {})}
              onApprove={onApprove}
              onReject={onReject}
              label={
                pendingCount === 1
                  ? "1 action awaiting approval on this event"
                  : `${pendingCount} actions awaiting approval on this event`
              }
              testId="event-bulk-approval"
            />
          )}
          {rulesToShow.length > 0 ? (
            <div className="space-y-1.5">
              {rulesToShow.map((r) => (
                <RuleOutcomeRow
                  key={`${r.rule_id}-${pendingIdFromOutcome(r) ?? r.outcome}`}
                  outcome={r}
                  pendingIds={activePendingIds}
                  onDecided={onDecided}
                  onApprove={onApprove}
                  onReject={onReject}
                  showApprovals={showApprovals}
                  hideRowApprovals={pendingCount > 1}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs italic text-slate-500 pt-2">
              {pendingCount === 0 && rulesTotal > 0
                ? "All actions on this event have been decided."
                : "No rules subscribed to this event type."}
            </p>
          )}
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
              Raw payload
            </summary>
            <pre className="mt-1.5 p-2 font-mono bg-slate-50 dark:bg-slate-900/60 rounded text-slate-700 dark:text-slate-300 overflow-x-auto">
              {JSON.stringify(
                { entity_refs: event.entity_refs, payload: event.payload },
                null,
                2
              )}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

function RuleOutcomeRow({
  outcome,
  pendingIds,
  onDecided,
  onApprove,
  onReject,
  showApprovals,
  hideRowApprovals,
}: {
  outcome: TriggerEventRuleOutcome;
  pendingIds: ReadonlySet<string>;
  onDecided?: () => void;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  showApprovals: boolean;
  hideRowApprovals: boolean;
}) {
  const meta = ruleMeta(outcome, pendingIds);
  const pendingId = pendingIdFromOutcome(outcome);
  const stillPending = isStillPending(outcome, pendingIds);
  const catalogueKey =
    typeof outcome.detail?.catalogue_key === "string"
      ? outcome.detail.catalogue_key
      : null;

  return (
    <div
      className={`px-2 py-2 rounded space-y-2 ${
        stillPending
          ? "bg-amber-50/80 dark:bg-amber-900/15 border border-amber-200/80 dark:border-amber-800/40"
          : "bg-slate-50 dark:bg-slate-900/40"
      }`}
    >
      <div className="flex items-start gap-3">
        <Badge color={meta.color} size="sm">
          <FontAwesomeIcon icon={meta.icon} className="mr-1" />
          {meta.label}
        </Badge>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-700 dark:text-slate-200">
            {outcome.rule_name}
          </p>
          {(outcome.action_type || catalogueKey) && (
            <p className="text-[11px] font-mono text-slate-500 dark:text-slate-400 mt-0.5">
              {outcome.action_type}
              {catalogueKey ? ` → ${catalogueKey}` : ""}
            </p>
          )}
        </div>
      </div>

      {stillPending && showApprovals && pendingId && !hideRowApprovals && (
        <ApprovalControls
          pendingId={pendingId}
          onDecided={onDecided ?? (() => {})}
          onApprove={onApprove}
          onReject={onReject}
          compact
        />
      )}
    </div>
  );
}

function PayloadSummary({ payload }: { payload: TriggerEventRowType["payload"] }) {
  const was = payload.old_value_was;
  const is = payload.new_value_is;
  if (was === undefined && is === undefined) return null;
  return (
    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
      {formatPayloadField(was)} → {formatPayloadField(is)}
    </p>
  );
}

function formatPayloadField(value: TriggerEventRowType["payload"][string]): string {
  if (value === null || value === undefined) return "∅";
  if (typeof value === "string") return value.length > 30 ? `${value.slice(0, 30)}…` : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatTime(d: Date): string {
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}
